import { createContext, useContext, useMemo, useState } from 'react';
import { AutogiroMandateResponse } from 'src/modules/invoice/models/autogiroMandateResponse';
import { InvoiceCountStats } from 'src/modules/invoice/models/invoiceCountStats';

export interface InvoiceContextSettings {
  createFileMode: boolean;
  setCreateFileMode: (state: boolean) => void;
  selectedAutogiroMandates: AutogiroMandateResponse[] | [];
  setSelectedAutogiroMandates: (mandates: AutogiroMandateResponse[] | []) => void;
  invoiceCountStats: InvoiceCountStats | undefined;
  setInvoiceCountStats: (stats: InvoiceCountStats | undefined) => void;
}

export const InvoiceContext = createContext<InvoiceContextSettings>({
  createFileMode: false,
  setCreateFileMode: () => undefined,
  selectedAutogiroMandates: [],
  setSelectedAutogiroMandates: () => undefined,
  invoiceCountStats: undefined,
  setInvoiceCountStats: () => undefined,
});

export const useInvoiceContext = () => {
  const context = useContext(InvoiceContext);
  if (context === undefined) {
    throw new Error('useInvoiceContext must be within AppContext');
  }
  return context;
};

interface Props {
  children: JSX.Element;
}
export const InvoiceContextProvider = ({ children }: Props) => {
  const [createFileMode, setCreateFileMode] = useState<boolean>(false);
  const [selectedAutogiroMandates, setSelectedAutogiroMandates] = useState<AutogiroMandateResponse[] | []>([]);
  const [invoiceCountStats, setInvoiceCountStats] = useState<InvoiceCountStats>();

  const invoiceContext = useMemo(
    () => ({
      createFileMode,
      setCreateFileMode,
      selectedAutogiroMandates,
      setSelectedAutogiroMandates,
      invoiceCountStats,
      setInvoiceCountStats,
    }),
    [createFileMode, selectedAutogiroMandates, invoiceCountStats],
  );

  return <InvoiceContext.Provider value={invoiceContext}>{children}</InvoiceContext.Provider>;
};
